/**
 * Arrow-key navigation for a `role="tablist"`, per the WAI-ARIA tabs pattern.
 *
 * react-bootstrap's `<Tabs>` handled ArrowLeft/ArrowRight itself. The
 * hand-rolled button strips that replaced it lost that, leaving tabs reachable
 * only by mouse or by tabbing through every one of them. This restores the
 * keyboard contract without bringing a tabs library back.
 *
 * It reads the tabs out of the DOM at keydown rather than taking a list, so a
 * strip adopts it with one line and no knowledge of how its own state is held:
 *
 *   <div role="tablist" onKeyDown={handleTablistKeys}>
 *
 * Selection follows focus — the pattern's default for tabs whose panels are
 * cheap to render — by clicking the target, which runs whatever handler the
 * strip already has. Wraps at both ends; Home/End jump to first/last.
 */
export function handleTablistKeys(event) {
  const step = { ArrowRight: 1, ArrowLeft: -1 }[event.key];
  const edge = { Home: 'first', End: 'last' }[event.key];
  if (step === undefined && edge === undefined) return;

  const tabs = Array.from(event.currentTarget.querySelectorAll('[role="tab"]')).filter(
    (t) => !t.disabled && t.getAttribute('aria-disabled') !== 'true',
  );
  if (tabs.length < 2) return;

  // Prefer where focus already is; fall back to the selected tab when the
  // keypress arrived from somewhere else inside the strip.
  const focused = tabs.indexOf(document.activeElement);
  const from =
    focused !== -1 ? focused : tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true');
  if (from === -1) return;

  const to =
    edge === 'first'
      ? 0
      : edge === 'last'
        ? tabs.length - 1
        : (from + step + tabs.length) % tabs.length;

  event.preventDefault();
  tabs[to].focus();
  tabs[to].click();
}
